/**
 * Sites v2 — shared model for the seven creator website templates
 * (T1–T7), the editor and the public renderer.
 *
 * Source: design_handoff_orbit/README.md + web/orbit-core.js. The
 * `SiteConfig` keys are the handoff's editor ↔ site contract:
 *
 *   brandName, headingFont, bodyFont, accent, darkMode,
 *   heroAlign, galleryLayout, navStyle, logoPlacement,
 *   sectionOrder, stickyCta, eyebrow, headline, sub, cta1, cta2, heroImage
 *
 * …plus the per-page copy every template ships with (About, Services
 * intro, Inquire steps, footer), so a studio can make the site theirs
 * without touching code. Everything is optional in storage; the
 * template's defaults fill the gaps at read time (`resolveConfig`).
 */

// ─────────────────────────────────────────────────────────────────────
// Templates
// ─────────────────────────────────────────────────────────────────────

export const SITE_TEMPLATE_IDS = ['t1', 't2', 't3', 't4', 't5', 't6', 't7'] as const;
export type SiteTemplateId = (typeof SITE_TEMPLATE_IDS)[number];

export const isSiteTemplateId = (v: unknown): v is SiteTemplateId =>
  typeof v === 'string' && (SITE_TEMPLATE_IDS as readonly string[]).includes(v);

export type HeroAlign = 'left' | 'center' | 'right';
export type GalleryLayout = 'grid' | 'masonry' | 'full';
export type NavStyle = 'bar' | 'hamburger' | 'stacked';
export type LogoPlacement = 'left' | 'center' | 'right';

export const HOME_SECTION_KEYS = ['work', 'reel', 'services', 'testimonials', 'journal', 'contact'] as const;
export type HomeSectionKey = (typeof HOME_SECTION_KEYS)[number];
export const HOME_SECTION_LABELS: Record<HomeSectionKey, string> = {
  work: 'Selected work',
  reel: 'Showreel',
  services: 'Services',
  testimonials: 'Testimonials',
  journal: 'Journal',
  contact: 'Contact',
};

export const SITE_PAGE_KEYS = ['home', 'work', 'project', 'reel', 'about', 'services', 'journal', 'post', 'inquire'] as const;
export type SitePageKey = (typeof SITE_PAGE_KEYS)[number];
/** Pages that appear in the nav, in order (orbit-core PAGES). */
export const NAV_PAGE_KEYS = ['work', 'reel', 'about', 'services', 'journal', 'inquire'] as const;
export type NavPageKey = (typeof NAV_PAGE_KEYS)[number];

// ─────────────────────────────────────────────────────────────────────
// Fonts (orbit-core FONTS / SERIF)
// ─────────────────────────────────────────────────────────────────────

export const SITE_FONTS = [
  'Archivo', 'Bebas Neue', 'IBM Plex Sans', 'Cormorant Garamond', 'Newsreader', 'Bodoni Moda',
  'Jost', 'Marcellus', 'Karla', 'Anton', 'Space Mono', 'Space Grotesk', 'Syne', 'Manrope',
  'Instrument Serif', 'DM Sans', 'Playfair Display', 'Work Sans',
] as const;
export type SiteFont = (typeof SITE_FONTS)[number] | (string & {});

const SERIF_FONTS = new Set<string>([
  'Cormorant Garamond', 'Newsreader', 'Bodoni Moda', 'Marcellus', 'Instrument Serif', 'Playfair Display',
]);

/** `stack()` from orbit-core: quoted family + generic fallback. */
export const siteFontStack = (font: string): string =>
  `'${font}', ${SERIF_FONTS.has(font) ? 'Georgia, serif' : 'Helvetica, Arial, sans-serif'}`;

/** Google Fonts stylesheet for a set of families — same axes the
 *  prototype requests (300–800 + italic 400) so light display
 *  weights render as designed. */
export const siteFontsHref = (fonts: Array<string | null | undefined>): string | null => {
  const uniq = Array.from(new Set(fonts.filter((f): f is string => !!f)));
  if (!uniq.length) return null;
  const fam = uniq
    .map((f) => `family=${encodeURIComponent(f).replace(/%20/g, '+')}:ital,wght@0,300;0,400;0,500;0,600;0,700;0,800;1,400`)
    .join('&');
  return `https://fonts.googleapis.com/css2?${fam}&display=swap`;
};

// ─────────────────────────────────────────────────────────────────────
// Palette + theme
// ─────────────────────────────────────────────────────────────────────

export interface SitePalette {
  bg: string;
  fg: string;
  mute: string;
  line: string;
  card: string;
}

// ─────────────────────────────────────────────────────────────────────
// Per-page copy (editable; template supplies defaults)
// ─────────────────────────────────────────────────────────────────────

export interface SiteCopy {
  home: {
    workEyebrow: string;
    workTitle: string;
    reelEyebrow: string;
    reelLink: string;
    servicesEyebrow: string;
    servicesLink: string;
    journalEyebrow: string;
    contactEyebrow: string;
    contactTitle: string;
    contactThanks: string;
    contactButton: string;
    contactPlaceholder: string;
  };
  nav: { book: string; bookCall: string; menu: string; close: string };
  work: { title: string; filters: string };
  reel: { title: string; label: string; play: string; allFilms: string };
  about: {
    eyebrow: string;
    title: string;
    body1: string;
    body2: string;
    facts: string[];
  };
  services: { title: string; intro: string; cta: string };
  journal: { title: string; read: string };
  inquire: {
    eyebrow: string;
    step1Title: string;
    dateLabel: string;
    datePlaceholder: string;
    step2Title: string;
    notesPlaceholder: string;
    step3Title: string;
    continueLabel: string;
    backLabel: string;
    sendLabel: string;
    doneTitle: string;
    doneBody: string;
    doneLink: string;
    emptySummary: string;
    types: string[];
    budgets: string[];
  };
  project: { back: string; next: string; client: string; role: string; where: string; year: string };
  footer: { newsletterBlurb: string; subscribed: string; join: string; builtOn: string };
  /** Design-specific labels a template needs beyond the shared set
   *  (chapter names, section kickers, button verbs…). Each template
   *  ships its own defaults; the editor can override any key. */
  x: Record<string, string>;
}

// ─────────────────────────────────────────────────────────────────────
// Config (stored in studio_sites.site_config — every key optional)
// ─────────────────────────────────────────────────────────────────────

export interface SiteSectionSetting {
  key: HomeSectionKey;
  visible: boolean;
}

export interface SiteContact {
  email?: string | null;
  phone?: string | null;
  location?: string | null;
  instagram?: string | null;
  vimeo?: string | null;
}

export interface SiteSeo {
  title?: string | null;
  description?: string | null;
  ogImage?: string | null;
}

/** A library media reference frozen into config / snapshots. */
export interface SiteMediaRef {
  id: string;
  kind: 'image' | 'video';
  url: string | null;
  playback_id?: string | null;
  width?: number | null;
  height?: number | null;
  alt?: string | null;
  duration_ms?: number | null;
  caption?: string | null;
}

/** Deep-partial copy override. */
export type SiteCopyPatch = { [K in keyof SiteCopy]?: Partial<SiteCopy[K]> };

export interface SiteConfig {
  version?: 2;
  // Brand
  brandName?: string;
  // Design
  headingFont?: string;
  bodyFont?: string;
  accent?: string;
  darkMode?: boolean;
  heroAlign?: HeroAlign;
  galleryLayout?: GalleryLayout;
  navStyle?: NavStyle;
  logoPlacement?: LogoPlacement;
  // Sections
  sections?: SiteSectionSetting[];
  stickyCta?: boolean;
  newsletter?: boolean;
  multiStepInquiry?: boolean;
  // Hero
  eyebrow?: string;
  headline?: string;
  sub?: string;
  cta1?: string;
  cta2?: string;
  heroImage?: string | null;
  heroMedia?: SiteMediaRef | null;
  // Page media
  reelMedia?: SiteMediaRef | null;
  reelCover?: SiteMediaRef | null;
  aboutPortrait?: SiteMediaRef | null;
  // Copy + misc
  copy?: SiteCopyPatch;
  contact?: SiteContact;
  seo?: SiteSeo;
}

/** Fully-resolved config: template defaults ← stored config. */
export interface SiteConfigResolved {
  brandName: string;
  headingFont: string;
  bodyFont: string;
  accent: string;
  darkMode: boolean;
  heroAlign: HeroAlign;
  galleryLayout: GalleryLayout;
  navStyle: NavStyle;
  logoPlacement: LogoPlacement;
  sections: SiteSectionSetting[];
  /** Contract string — visible sections, in order. */
  sectionOrder: string;
  stickyCta: boolean;
  newsletter: boolean;
  multiStepInquiry: boolean;
  eyebrow: string;
  headline: string;
  sub: string;
  cta1: string;
  cta2: string;
  heroImage: string | null;
  heroMedia: SiteMediaRef | null;
  reelMedia: SiteMediaRef | null;
  reelCover: SiteMediaRef | null;
  aboutPortrait: SiteMediaRef | null;
  copy: SiteCopy;
  contact: SiteContact;
  seo: SiteSeo;
}

// ─────────────────────────────────────────────────────────────────────
// Template metadata
// ─────────────────────────────────────────────────────────────────────

export interface SiteTemplateMeta {
  id: SiteTemplateId;
  number: string; // "T1"
  name: string; // "Grid"
  subtitle: string; // "Gallery Quiet"
  structure: string; // README "Structure"
  type: string; // README "Type"
  palette: { light: SitePalette; dark: SitePalette };
  accents: string[];
  /** Config defaults (the file's `defaults` object). */
  defaults: Omit<SiteConfigResolved, 'sections' | 'sectionOrder' | 'copy' | 'contact' | 'seo' | 'heroMedia' | 'reelMedia' | 'reelCover' | 'aboutPortrait'> & {
    sectionOrder: string;
  };
  copy: SiteCopy;
  /** Home-hero fallback tone when there is no hero image. */
  heroTone: string;
  built: boolean;
}

const DEFAULT_SECTION_ORDER = 'work,reel,services,testimonials,journal,contact';

const T1_COPY: SiteCopy = {
  home: {
    workEyebrow: 'Selected work',
    workTitle: 'Eight projects, four years, one lens.',
    reelEyebrow: 'Showreel 2026',
    reelLink: 'All films',
    servicesEyebrow: 'Services',
    servicesLink: 'Full pricing',
    journalEyebrow: 'Journal',
    contactEyebrow: 'Get in touch',
    contactTitle: 'Booking 2027 weddings and autumn commissions.',
    contactThanks: 'Thank you. I reply within two working days.',
    contactButton: 'Send',
    contactPlaceholder: 'Tell me about the project',
  },
  nav: { book: 'Book', bookCall: 'Book a call', menu: 'Menu', close: 'Close' },
  work: { title: 'Work', filters: 'All · Weddings · Commercial · Film' },
  reel: { title: 'Films', label: 'Showreel · 1:48', play: 'Play', allFilms: 'All films' },
  about: {
    eyebrow: 'About',
    title: 'I photograph slowly and edit less than you think.',
    body1:
      'Based in Stockholm, working across the Nordics and southern Europe. Twelve years of weddings, brand films and long documentary projects, mostly on one 35mm lens.',
    body2: 'Clients include Fold, Atelier Mora, Nordiska Kompaniet, and around two hundred couples who trusted me with the day.',
    facts: ['12 yrs', '200+ weddings', '40 films'],
  },
  services: {
    title: 'Services & pricing',
    intro: 'Every commission is quoted individually. These are starting points so you know where we are.',
    cta: 'Inquire',
  },
  journal: { title: 'Journal', read: 'Read' },
  inquire: {
    eyebrow: 'Inquiry',
    step1Title: 'What are we making?',
    dateLabel: 'Date, or roughly when',
    datePlaceholder: 'e.g. 14 June 2027',
    step2Title: 'Budget and details',
    notesPlaceholder: 'Anything else: location, number of guests, references, deadlines.',
    step3Title: 'Where should I reply?',
    continueLabel: 'Continue',
    backLabel: 'Back',
    sendLabel: 'Send inquiry',
    doneTitle: 'Sent. Thank you.',
    doneBody: 'I reply within two working days, usually with a few questions and a short call to check we suit each other.',
    doneLink: 'Back to home',
    emptySummary: 'Nothing selected yet',
    types: ['Wedding', 'Portrait', 'Commercial', 'Brand film', 'Event', 'Real estate'],
    budgets: ['Under $1k', '$1k – $3k', '$3k – $8k', '$8k+'],
  },
  project: { back: '← Work', next: 'Next project', client: 'Client', role: 'Role', where: 'Where', year: 'Year' },
  footer: {
    newsletterBlurb: 'Occasional letters: new work, open dates, prints.',
    subscribed: 'Subscribed. Talk soon.',
    join: 'Join',
    builtOn: 'Built on Orbit',
  },
  x: {},
};

const baseDefaults = (over: Partial<SiteTemplateMeta['defaults']>): SiteTemplateMeta['defaults'] => ({
  brandName: 'Mara Lindqvist',
  headingFont: 'Archivo',
  bodyFont: 'Archivo',
  accent: '#161514',
  darkMode: false,
  heroAlign: 'left',
  galleryLayout: 'grid',
  navStyle: 'bar',
  logoPlacement: 'left',
  sectionOrder: DEFAULT_SECTION_ORDER,
  stickyCta: true,
  newsletter: true,
  multiStepInquiry: true,
  eyebrow: 'Photographer & filmmaker — Stockholm',
  headline: 'Quiet pictures of people, places, and the light between them.',
  sub: '',
  cta1: 'View work',
  cta2: 'Check availability',
  heroImage: null,
  ...over,
});

/** T4 Split — Luxury Minimal (fine-art wedding voice). */
const T4_COPY: SiteCopy = {
  home: {
    workEyebrow: 'Portfolio',
    workTitle: 'Recent celebrations',
    reelEyebrow: 'Film',
    reelLink: 'Watch the reel',
    servicesEyebrow: 'Collections',
    servicesLink: 'Full details',
    journalEyebrow: 'Journal',
    contactEyebrow: 'Enquire',
    contactTitle: '2027 has four dates remaining.',
    contactThanks: 'Thank you. Expect a reply within 48 hours.',
    contactButton: 'Send',
    contactPlaceholder: 'Wedding date and venue',
  },
  nav: { book: 'Reserve a date', bookCall: 'Book a call', menu: 'Menu', close: 'Close' },
  work: { title: 'Celebrations', filters: '' },
  reel: { title: 'Motion', label: 'Showreel', play: 'Play', allFilms: 'Watch the reel' },
  about: {
    eyebrow: 'About',
    title: 'Discreet, unhurried, and there for the whole day.',
    body1:
      'Based between Milan and Lake Como, photographing weddings across Italy, France and the Mediterranean for fourteen years. Medium format film for the portraits, digital for the dancing.',
    body2: "Featured in Vogue Sposa, Harper's Bazaar and Over the Moon. Twelve weddings a year, by invitation of the couple.",
    facts: ['14 Years', '160 Weddings', '12 Per year'],
  },
  services: {
    title: 'Investment',
    intro: 'Each collection is a starting point. Travel and albums are quoted separately.',
    cta: 'Enquire',
  },
  journal: { title: 'Notes', read: 'Read' },
  inquire: {
    eyebrow: 'Reserve a date',
    step1Title: 'Tell me about the celebration.',
    dateLabel: '',
    datePlaceholder: 'Date, or season and year',
    step2Title: 'Budget and details.',
    notesPlaceholder: 'Venue, guest count, anything else',
    step3Title: 'Where shall I reply?',
    continueLabel: 'Continue',
    backLabel: 'Back',
    sendLabel: 'Send enquiry',
    doneTitle: 'Thank you.',
    doneBody: 'A reply follows within 48 hours, with availability and a proposal to meet over a call.',
    doneLink: 'Return home',
    emptySummary: 'Nothing selected yet',
    types: ['Wedding', 'Portrait', 'Commercial', 'Brand film', 'Event', 'Real estate'],
    budgets: ['Under $1k', '$1k – $3k', '$3k – $8k', '$8k+'],
  },
  project: { back: '← Celebrations', next: 'Next', client: 'Client', role: 'Role', where: 'Where', year: 'Year' },
  footer: {
    newsletterBlurb: 'Seasonal letters with new work and open dates.',
    subscribed: 'Subscribed. Thank you.',
    join: 'Join',
    builtOn: 'Built on Orbit',
  },
  x: {
    workLink: 'View all',
    reelTitle: 'Super 8 and digital, edited to the music you chose.',
    servicesTitle: 'Three ways to work together',
    testimonialsEyebrow: 'Kind words',
    journalTitle: 'Notes on planning and light',
    namePlaceholder: 'Names',
    emailPlaceholder: 'Email',
  },
};

/** T5 Minimal — Warm Organic (family / lifestyle voice). */
const T5_COPY: SiteCopy = {
  home: {
    workEyebrow: '',
    workTitle: 'Recent sessions',
    reelEyebrow: 'Short films · 2026',
    reelLink: '▶ Play 1:48',
    servicesEyebrow: '',
    servicesLink: "What's included →",
    journalEyebrow: '',
    contactEyebrow: '',
    contactTitle: "Let's plan a morning together.",
    contactThanks: 'Got it. Talk soon!',
    contactButton: 'Send',
    contactPlaceholder: 'Who, where, roughly when',
  },
  nav: { book: 'Say hello', bookCall: 'Book a call', menu: 'Menu', close: 'Close' },
  work: { title: 'Sessions', filters: 'All · Families · Couples · Brands' },
  reel: { title: 'Little films', label: '1:48', play: '▶ Play reel', allFilms: 'Little films' },
  about: {
    eyebrow: "Hi, I'm Noor",
    title: 'I photograph the in-between bits.',
    body1:
      'Mother of two, terrible surfer, decent cook. I have been photographing families in Byron Bay and along the coast for nine years, always at the edges of the day when the light goes soft.',
    body2: 'Sessions are relaxed and short. Kids are allowed to be kids. Nobody has to say cheese.',
    facts: ['9 years', '400+ families', 'Golden hour only'],
  },
  services: {
    title: 'Sessions & pricing',
    intro: 'Simple packages. Prints and albums are extra, travel outside the shire is quoted.',
    cta: 'Book this',
  },
  journal: { title: 'Blog', read: 'Read →' },
  inquire: {
    eyebrow: 'Booking',
    step1Title: 'What kind of session?',
    dateLabel: '',
    datePlaceholder: 'When would you like to shoot?',
    step2Title: 'Budget and details',
    notesPlaceholder: "Who's coming, favourite beach, anything else",
    step3Title: 'Where should I reply?',
    continueLabel: 'Continue',
    backLabel: 'Back',
    sendLabel: 'Send booking request',
    doneTitle: 'Yay, got it!',
    doneBody: "I'll be in touch within a day or two with some dates. Keep an eye on your inbox.",
    doneLink: 'Back home',
    emptySummary: 'Nothing selected yet',
    types: ['Wedding', 'Portrait', 'Commercial', 'Brand film', 'Event', 'Real estate'],
    budgets: ['Under $1k', '$1k – $3k', '$3k – $8k', '$8k+'],
  },
  project: { back: '← All sessions', next: 'Next session', client: 'Client', role: 'Role', where: 'Where', year: 'Year' },
  footer: {
    newsletterBlurb: 'A monthly note with mini-session dates and new work.',
    subscribed: "You're in!",
    join: 'Join',
    builtOn: 'Built on Orbit',
  },
  x: {
    workLink: 'Everything →',
    reelTitle: 'A day at the water',
    servicesTitle: 'Sessions',
    journalTitle: 'From the blog',
    journalLink: 'All posts →',
    contactBody: "Tell me a little about who's coming and when. I reply within a day or two, usually with a few date options.",
    namePlaceholder: 'Your name',
    formNamePlaceholder: 'Name',
    emailPlaceholder: 'Email',
  },
};

const T2_COPY: SiteCopy = {
  home: {
    workEyebrow: 'Selected work',
    workTitle: 'Selected work',
    reelEyebrow: 'Showreel',
    reelLink: 'Watch',
    servicesEyebrow: 'What I do',
    servicesLink: 'All services',
    journalEyebrow: 'Notes',
    contactEyebrow: 'Contact',
    contactTitle: "Let's make\nsomething",
    contactThanks: "Received. I'll write back within 48 hours.",
    contactButton: 'Send',
    contactPlaceholder: 'Project, dates, budget range',
  },
  nav: { book: 'Start a project', bookCall: 'Book a call', menu: 'Menu', close: 'Close' },
  work: { title: 'Work', filters: 'All · Commercial · Music · Film' },
  reel: { title: 'Films', label: 'Showreel 2026', play: '▶', allFilms: 'All films' },
  about: {
    eyebrow: 'About',
    title: 'Camera first.\nThen the story.',
    body1: 'Fifteen years behind the lens for brands, bands, and directors who wanted a film that felt like a memory instead of an ad.',
    body2: 'Based in Los Angeles, shooting worldwide. Available for DP work, direction, and full-service production through a small trusted crew.',
    facts: ['15 years', '140 films', '2 features'],
  },
  services: {
    title: 'Services',
    intro: 'Starting rates. Every quote is built around crew, days, and deliverables.',
    cta: 'Inquire',
  },
  journal: { title: 'Notes', read: 'Read' },
  inquire: {
    eyebrow: 'Start a project',
    step1Title: 'Type of project',
    dateLabel: 'Shoot dates',
    datePlaceholder: 'Shoot dates, or roughly when',
    step2Title: 'Budget',
    notesPlaceholder: 'Deliverables, references, crew needs',
    step3Title: 'Contact',
    continueLabel: 'Continue',
    backLabel: 'Back',
    sendLabel: 'Send brief',
    doneTitle: 'Got it.',
    doneBody: 'Expect a reply within 48 hours with a first read on schedule and budget.',
    doneLink: 'Back to home →',
    emptySummary: 'Nothing selected yet',
    types: ['Wedding', 'Portrait', 'Commercial', 'Brand film', 'Event', 'Real estate'],
    budgets: ['Under $1k', '$1k – $3k', '$3k – $8k', '$8k+'],
  },
  project: { back: '← Work', next: 'Next', client: 'Client', role: 'Role', where: 'Location', year: 'Year' },
  footer: {
    newsletterBlurb: 'A short newsletter when new work drops. No more than monthly.',
    subscribed: "You're on the list.",
    join: 'Join',
    builtOn: 'Built on Orbit',
  },
  x: {
    /** Home "Selected work" header link. */
    workLink: 'All projects →',
    /** Client marquee under the hero, comma-separated (empty hides it). */
    clients: 'Netflix, Nike, Fold Agency, A24, Atelier Mora, Sony Music, Patagonia',
    /** Home showreel section ("\n" = line break). */
    reelTitle: 'Two years,\n108 seconds.',
    reelBlurb: 'Commercials, music videos and two features cut together at the pace they were shot.',
    /** Last line of the contact title, in the accent colour. */
    contactAccent: 'loud.',
    /** Inquire page display title ("\n" = line break). */
    inquireTitle: 'Tell me\nthe brief.',
  },
};

const T6_COPY: SiteCopy = {
  home: {
    // Numbered section headers (T6 renders them as the section h2).
    workEyebrow: '',
    workTitle: '01 / Work',
    reelEyebrow: '02 / Reel',
    reelLink: '',
    servicesEyebrow: '03 / Rates',
    servicesLink: 'Details →',
    journalEyebrow: '04 / Log',
    contactEyebrow: '',
    contactTitle: 'Got a\nbrief?',
    contactThanks: 'Received. Talk soon.',
    contactButton: 'Send →',
    contactPlaceholder: 'BRIEF / DATES / BUDGET',
  },
  nav: { book: 'Hire me →', bookCall: 'Book a call →', menu: '[ Menu ]', close: '[ Close ]' },
  work: { title: 'Index', filters: '08 projects · 2024–2026' },
  reel: { title: 'Motion', label: '01:48 · 4K · 2026', play: 'Play', allFilms: '06 films' },
  about: {
    eyebrow: '// About',
    title: 'No filters.\nNo fixers.\nOne camera.',
    body1: 'Ten years shooting for streetwear brands, labels and magazines that wanted the real thing. Berlin-based, works anywhere with a power outlet.',
    body2: 'Selected: Nike, Boiler Room, 032c, Highsnobiety, Warp Records.',
    /** "value label" pairs: big value, mute label. */
    facts: ['10 Years', '60+ Campaigns', '1 Lens'],
  },
  services: {
    title: 'Rates',
    intro: 'Day rates. Usage and travel billed separately.',
    cta: 'Book →',
  },
  journal: { title: 'Log', read: 'Read →' },
  inquire: {
    eyebrow: '// Brief',
    step1Title: '01 — What',
    dateLabel: '',
    datePlaceholder: 'DATES',
    step2Title: '02 — Budget',
    notesPlaceholder: 'BRIEF / DELIVERABLES / REFERENCES',
    step3Title: '03 — Contact',
    continueLabel: 'Next →',
    backLabel: '← Back',
    sendLabel: 'Send brief →',
    doneTitle: 'Sent.',
    doneBody: 'Reply inside 24h. Check spam, my emails are blunt.',
    doneLink: '← Home',
    emptySummary: 'Nothing selected yet',
    types: ['Wedding', 'Portrait', 'Commercial', 'Brand film', 'Event', 'Real estate'],
    budgets: ['Under $1k', '$1k – $3k', '$3k – $8k', '$8k+'],
  },
  project: { back: '← Index', next: 'Next →', client: 'Client', role: 'Role', where: 'Where', year: 'Year' },
  footer: {
    newsletterBlurb: 'Newsletter. Irregular. Unsubscribe anytime.',
    subscribed: "OK. You're in.",
    join: 'Join',
    builtOn: 'Built on Orbit',
  },
  x: {
    /** Home section links. */
    workLink: 'Index →',
    journalLink: 'All →',
    /** Home hero fact strip. */
    clientsLabel: 'Clients',
    clients: 'Nike · Boiler Room · 032c',
    kitLabel: 'Kit',
    kit: 'Leica M6 · FX3 · Bolex',
    /** Home contact panel blurb. */
    contactBlurb: 'Send the basics. I answer within 24h, no account managers, no decks.',
    /** Inquire page display title ("\n" = line break). */
    inquireTitle: 'Hire\nme.',
    /** ≤760px bar + overlay labels (the desktop ones are bracketed). */
    mobileMenu: 'Menu',
    mobileClose: 'Close',
    mobileBook: 'Book a call',
  },
};

const T3_COPY: SiteCopy = {
  home: {
    workEyebrow: 'Stories',
    workTitle: 'Selected stories',
    reelEyebrow: 'Moving pictures',
    reelLink: 'Watch the 2026 reel',
    servicesEyebrow: 'Commissions',
    servicesLink: 'Full rates',
    journalEyebrow: 'From the journal',
    contactEyebrow: 'Correspondence',
    contactTitle: 'Write to me about your day.',
    contactThanks: 'Thank you. Your letter has arrived.',
    contactButton: 'Send letter',
    contactPlaceholder: 'Dates, place, what you have in mind',
  },
  nav: { book: 'Enquire', bookCall: 'Book a call', menu: 'Menu', close: 'Close' },
  work: { title: 'Stories, 2024 — 2026', filters: 'Weddings · Portraits · Films' },
  reel: { title: 'Films', label: 'Play reel', play: 'Play', allFilms: 'All films' },
  about: {
    eyebrow: 'A note from the photographer',
    title: 'I am interested in what happens when nobody is performing.',
    body1:
      'Eleven years photographing weddings, families and makers across the north Atlantic. I work alone or with one second shooter, on film and digital, and I stay for the whole day.',
    body2: 'Published in Kinfolk, Cereal and The Gentlewoman. Represented for commercial work by Fold.',
    facts: ['11 years', '190 weddings', '3 books'],
  },
  services: {
    title: 'Commissions',
    intro: 'Starting rates. Each is quoted for the day, the place and the deliverables you need.',
    cta: 'Enquire',
  },
  journal: { title: 'Journal', read: 'Continue reading' },
  inquire: {
    eyebrow: 'Enquiry',
    step1Title: 'What shall we photograph?',
    dateLabel: 'The date',
    datePlaceholder: 'The date, or roughly when',
    step2Title: 'Budget and particulars',
    notesPlaceholder: 'Where, how many, and anything you would like me to know',
    step3Title: 'Where shall I write?',
    continueLabel: 'Continue',
    backLabel: 'Back',
    sendLabel: 'Send enquiry',
    doneTitle: 'Received, with thanks.',
    doneBody: 'I reply within two days, usually with a few questions and a proposal for a call.',
    doneLink: 'Return home',
    emptySummary: 'Nothing selected yet',
    types: ['Wedding', 'Portrait', 'Commercial', 'Brand film', 'Event', 'Real estate'],
    budgets: ['Under $1k', '$1k – $3k', '$3k – $8k', '$8k+'],
  },
  project: { back: '← Stories', next: 'Next story:', client: 'Client', role: 'Role', where: 'Place', year: 'Year' },
  footer: {
    newsletterBlurb: 'A quarterly letter: new stories, open dates, prints.',
    subscribed: 'Subscribed. The next letter arrives in October.',
    join: 'Subscribe',
    builtOn: 'Built on Orbit',
  },
  x: {
    /** Masthead line under the brand (stacked nav). */
    tagline: 'Photographs & films · Est. 2014',
    /** Caption under the lead photograph (empty hides it). */
    heroCaption: 'Fig. 1 — Ines & Theo, Isle of Harris, June 2026.',
    /** Chapter links. */
    workLink: 'See the archive',
    journalLink: 'All entries',
    /** "Moving pictures" chapter sentence; the reel link follows it. */
    reelBlurb: 'Films are cut like letters: unhurried, in order, with room to breathe.',
    /** Ornament above the testimonials. */
    quoteMark: '”',
    /** Under the contact title. */
    contactBlurb: 'I take on twenty weddings a year and a handful of longer commissions. Replies within two days.',
    /** Work page eyebrow. */
    archiveEyebrow: 'The archive',
    /** Under the Films title. */
    reelIntro: 'The 2026 reel, and six longer pieces.',
  },
};

const T7_COPY: SiteCopy = {
  home: {
    workEyebrow: '',
    workTitle: 'Features',
    reelEyebrow: 'Motion',
    reelLink: 'All films',
    servicesEyebrow: 'Services',
    servicesLink: 'Pricing →',
    journalEyebrow: 'Columns',
    contactEyebrow: 'Contact',
    contactTitle: "Got a launch? Let's shoot it.",
    contactThanks: 'Got it. Talk tomorrow.',
    contactButton: 'Send brief',
    contactPlaceholder: 'Brand, deliverables, deadline',
  },
  nav: { book: 'Work with us', bookCall: 'Book a call', menu: 'Menu', close: 'Close' },
  work: { title: 'Work', filters: 'All · Stills · Motion · Social' },
  reel: { title: 'Motion', label: '1:48', play: '▶ Play reel', allFilms: 'All films' },
  about: {
    eyebrow: 'The studio',
    title: 'Two people, one van, every brief on time.',
    body1: 'Founded in 2018 by a photographer and an editor who kept getting hired for the same jobs. We shoot stills and motion in the same session so your campaign matches on every channel.',
    body2: 'Clients include Glossier, Allbirds, Bon Appétit, Fold and a dozen DTC brands you have seen on your feed this week.',
    /** "value label" pairs: big value, mute label. */
    facts: ['8 Years', '210 Campaigns', '48h Rush turnaround'],
  },
  services: {
    title: 'Pricing',
    intro: 'Starting points. Usage, retouching and travel quoted per job.',
    cta: 'Get a quote',
  },
  journal: { title: 'Columns', read: 'Read' },
  inquire: {
    eyebrow: 'Brief',
    step1Title: 'Project type',
    dateLabel: '',
    datePlaceholder: 'Shoot date or deadline',
    step2Title: 'Budget range',
    notesPlaceholder: 'Deliverables, channels, references',
    step3Title: 'Contact',
    continueLabel: 'Continue',
    backLabel: 'Back',
    sendLabel: 'Send brief',
    doneTitle: 'Brief received.',
    doneBody: 'We reply within one business day with a first estimate and available dates.',
    doneLink: 'Back to home →',
    emptySummary: 'Nothing selected yet',
    types: ['Wedding', 'Portrait', 'Commercial', 'Brand film', 'Event', 'Real estate'],
    budgets: ['Under $1k', '$1k – $3k', '$3k – $8k', '$8k+'],
  },
  project: { back: '← Work', next: 'Next feature', client: 'Client', role: 'Role', where: 'Location', year: 'Year' },
  footer: {
    newsletterBlurb: 'The monthly issue: new campaigns, behind the scenes, open dates.',
    subscribed: 'Subscribed. First issue lands next month.',
    join: 'Subscribe',
    builtOn: 'Built on Orbit',
  },
  x: {
    /** Header masthead note (bar nav). */
    issue: 'Issue 07 — Fall 2026',
    /** Hero: cover-story tag + the "now booking" tile. */
    coverLabel: 'Cover story',
    bookingLabel: 'Now booking',
    bookingValue: 'Q4 2026',
    /** Home section links + reel blurb. */
    workLink: 'All work →',
    reelTitle: 'The 2026 reel, 108 seconds.',
    reelBlurb: 'Product, fashion and food. Cut for sound-on, works with sound off.',
    journalLink: 'Archive →',
    /** Home contact panel blurb. */
    contactBlurb: 'Reply within one business day. Rush jobs welcome, quoted honestly.',
    /** Inquire page display title + the step-3 email placeholder. */
    inquireTitle: "Tell us what you're launching.",
    emailPlaceholder: 'Work email',
  },
};

export const SITE_TEMPLATES: Record<SiteTemplateId, SiteTemplateMeta> = {
  t1: {
    id: 't1',
    number: 'T1',
    name: 'Grid',
    subtitle: 'Gallery Quiet',
    structure: '3-col image grid, tiny caps',
    type: 'Archivo 300/400',
    palette: {
      light: { bg: '#fbfaf8', fg: '#161514', mute: '#8a8680', line: 'rgba(22,21,20,.12)', card: '#f1efeb' },
      dark: { bg: '#141413', fg: '#ece9e3', mute: '#8b877f', line: 'rgba(236,233,227,.14)', card: '#1d1c1a' },
    },
    accents: ['#161514', '#8a6a3f', '#3d5a4c', '#7a3b2e'],
    defaults: baseDefaults({}),
    copy: T1_COPY,
    heroTone: 'linear-gradient(135deg,#c9c2b8,#8f8577)',
    built: true,
  },
  t2: {
    id: 't2',
    number: 'T2',
    name: 'Full-Bleed',
    subtitle: 'Cinematic',
    structure: '100vh hero, edge-to-edge frames, dark default',
    type: 'Bebas Neue + IBM Plex Sans',
    palette: {
      light: { bg: '#f4f2ee', fg: '#121212', mute: '#7d7a74', line: 'rgba(18,18,18,.14)', card: '#ebe8e2' },
      dark: { bg: '#0b0b0c', fg: '#f2efe9', mute: '#8d8a84', line: 'rgba(242,239,233,.14)', card: '#151517' },
    },
    accents: ['#d8a25a', '#e0523a', '#5ab3c9', '#b6d85a'],
    defaults: baseDefaults({
      brandName: 'Kova Films',
      headingFont: 'Bebas Neue',
      bodyFont: 'IBM Plex Sans',
      accent: '#d8a25a',
      darkMode: true,
      heroAlign: 'left',
      galleryLayout: 'full',
      navStyle: 'bar',
      logoPlacement: 'left',
      sectionOrder: DEFAULT_SECTION_ORDER,
      stickyCta: true,
      eyebrow: 'Director of photography · Los Angeles',
      headline: 'Stories shot in one breath.',
      sub: '',
      cta1: 'Watch reel · 1:48',
      cta2: 'Selected work ↓',
    }),
    copy: T2_COPY,
    heroTone: 'linear-gradient(160deg,#2b2b2f 0%,#0d0d0e 100%)',
    built: true,
  },
  t3: {
    id: 't3',
    number: 'T3',
    name: 'Editorial',
    subtitle: 'Serif',
    structure: 'stacked masthead, numbered chapters, captions',
    type: 'Cormorant Garamond + Newsreader',
    palette: {
      light: { bg: '#f6f1e8', fg: '#1f1b17', mute: '#877d70', line: 'rgba(31,27,23,.16)', card: '#ede6da' },
      dark: { bg: '#1c1916', fg: '#efe7da', mute: '#9a8f80', line: 'rgba(239,231,218,.16)', card: '#26221e' },
    },
    accents: ['#8a3b2a', '#3f5a3a', '#2f4a6b', '#8a6a2a'],
    defaults: baseDefaults({
      brandName: 'Eilidh Macrae',
      headingFont: 'Cormorant Garamond',
      bodyFont: 'Newsreader',
      accent: '#8a3b2a',
      darkMode: false,
      heroAlign: 'center',
      galleryLayout: 'masonry',
      navStyle: 'stacked',
      logoPlacement: 'center',
      sectionOrder: DEFAULT_SECTION_ORDER,
      stickyCta: true,
      eyebrow: 'Volume IV · Autumn 2026',
      headline: 'The light arrives late in the north, and stays.',
      sub: 'Weddings, portraits and documentary films photographed slowly across Scotland, Ireland and the Faroe Islands.',
      cta1: '',
      cta2: '',
    }),
    copy: T3_COPY,
    heroTone: 'linear-gradient(135deg,#c8bfb0,#7d7264)',
    built: true,
  },
  t4: {
    id: 't4',
    number: 'T4',
    name: 'Split',
    subtitle: 'Luxury Minimal',
    structure: 'half image / half type, letterspaced caps',
    type: 'Bodoni Moda + Jost',
    palette: {
      light: { bg: '#f7f6f3', fg: '#1c1b19', mute: '#8b877f', line: 'rgba(28,27,25,.1)', card: '#eeece7' },
      dark: { bg: '#161513', fg: '#f0ede7', mute: '#8f8b83', line: 'rgba(240,237,231,.12)', card: '#1f1e1b' },
    },
    accents: ['#9c8a63', '#7d8a7a', '#8c6f7a', '#6f7d8c'],
    defaults: baseDefaults({
      brandName: 'Serra',
      headingFont: 'Bodoni Moda',
      bodyFont: 'Jost',
      accent: '#9c8a63',
      darkMode: false,
      heroAlign: 'left',
      galleryLayout: 'grid',
      navStyle: 'bar',
      logoPlacement: 'center',
      sectionOrder: DEFAULT_SECTION_ORDER,
      stickyCta: true,
      eyebrow: 'Fine art wedding photography · Lake Como & beyond',
      headline: 'An heirloom, made the day it happens.',
      sub: 'Twelve weddings a year, photographed on medium format film, delivered as a bound volume and a private gallery.',
      cta1: 'Reserve a date',
      cta2: 'The portfolio',
    }),
    copy: T4_COPY,
    heroTone: 'linear-gradient(160deg,#d6d1c7,#8c867b)',
    built: true,
  },
  t5: {
    id: 't5',
    number: 'T5',
    name: 'Minimal',
    subtitle: 'Warm Organic',
    structure: 'pill nav, 24px radii, arch crops',
    type: 'Marcellus + Karla',
    palette: {
      light: { bg: '#efe8dd', fg: '#2b241d', mute: '#8a7d6d', line: 'rgba(43,36,29,.12)', card: '#e6dccd' },
      dark: { bg: '#1f1a15', fg: '#f1e9dd', mute: '#9a8d7c', line: 'rgba(241,233,221,.12)', card: '#2b251e' },
    },
    accents: ['#b8623f', '#7a8a5a', '#5a7a8a', '#a86a7a'],
    defaults: baseDefaults({
      brandName: 'Noor & Co.',
      headingFont: 'Marcellus',
      bodyFont: 'Karla',
      accent: '#b8623f',
      darkMode: false,
      heroAlign: 'center',
      galleryLayout: 'grid',
      navStyle: 'bar',
      logoPlacement: 'left',
      sectionOrder: DEFAULT_SECTION_ORDER,
      stickyCta: true,
      eyebrow: 'Family & lifestyle photography · Byron Bay',
      headline: 'Sun-warmed, a little messy, completely yours.',
      sub: 'Golden-hour sessions for families, couples and small brands who would rather laugh than pose.',
      cta1: 'Book a session',
      cta2: 'See the work',
    }),
    copy: T5_COPY,
    heroTone: 'linear-gradient(160deg,#d9c9b3,#a58d6f)',
    built: true,
  },
  t6: {
    id: 't6',
    number: 'T6',
    name: 'Dark',
    subtitle: 'Brutalist',
    structure: '2px rules, mono labels, dark default',
    type: 'Anton + Space Mono',
    palette: {
      light: { bg: '#f0f0ec', fg: '#0e0e0e', mute: '#6f6f6a', line: '#0e0e0e', card: '#e4e4de' },
      dark: { bg: '#0e0e0e', fg: '#f0f0ec', mute: '#8a8a84', line: '#f0f0ec', card: '#181818' },
    },
    accents: ['#d9ff3d', '#ff4d2e', '#3dd9ff', '#ff3dcb'],
    defaults: baseDefaults({
      brandName: 'Jonas Reck',
      headingFont: 'Anton',
      bodyFont: 'Space Mono',
      accent: '#d9ff3d',
      darkMode: true,
      heroAlign: 'left',
      galleryLayout: 'grid',
      navStyle: 'bar',
      logoPlacement: 'left',
      sectionOrder: DEFAULT_SECTION_ORDER,
      stickyCta: true,
      eyebrow: '// Photographer + video · Berlin · Available Q4 _',
      headline: 'Raw. Loud. Unretouched.',
      sub: '',
      cta1: 'Work ↓',
      cta2: 'Reel ▶',
    }),
    copy: T6_COPY,
    heroTone: 'linear-gradient(135deg,#3a3a3a,#111)',
    built: true,
  },
  t7: {
    id: 't7',
    number: 'T7',
    name: 'Magazine',
    subtitle: 'Bold Sans',
    structure: 'cover-story hero, 6-col asymmetric feature grid',
    type: 'Syne 800 + Manrope',
    palette: {
      light: { bg: '#f4f4f0', fg: '#111114', mute: '#71717a', line: 'rgba(17,17,20,.14)', card: '#e9e9e3' },
      dark: { bg: '#111114', fg: '#f4f4f0', mute: '#8c8c94', line: 'rgba(244,244,240,.16)', card: '#1b1b20' },
    },
    accents: ['#2b4bff', '#ff3b2b', '#00a86b', '#ff8a00'],
    defaults: baseDefaults({
      brandName: 'Studio Vance',
      headingFont: 'Syne',
      bodyFont: 'Manrope',
      accent: '#2b4bff',
      darkMode: false,
      heroAlign: 'left',
      galleryLayout: 'masonry',
      navStyle: 'bar',
      logoPlacement: 'left',
      sectionOrder: DEFAULT_SECTION_ORDER,
      stickyCta: true,
      eyebrow: 'Commercial photography & motion · NYC',
      headline: 'Pictures that sell without asking.',
      sub: 'A two-person studio making campaign stills, product film and social cut-downs for brands that need to move fast and look expensive.',
      cta1: 'See the work',
      cta2: 'Book a shoot',
    }),
    copy: T7_COPY,
    heroTone: 'linear-gradient(135deg,#c9c9d1,#6f6f7c)',
    built: true,
  },
};

export const SITE_TEMPLATE_LIST: SiteTemplateMeta[] = SITE_TEMPLATE_IDS.map((id) => SITE_TEMPLATES[id]);

export const getSiteTemplate = (id: string | null | undefined): SiteTemplateMeta =>
  (id && isSiteTemplateId(id) ? SITE_TEMPLATES[id] : SITE_TEMPLATES.t1);

// ─────────────────────────────────────────────────────────────────────
// Config resolution
// ─────────────────────────────────────────────────────────────────────

const str = (v: unknown, fallback: string): string => (typeof v === 'string' ? v : fallback);
const bool = (v: unknown, fallback: boolean): boolean => (typeof v === 'boolean' ? v : fallback);
const oneOf = <T extends string>(v: unknown, opts: readonly T[], fallback: T): T =>
  typeof v === 'string' && (opts as readonly string[]).includes(v) ? (v as T) : fallback;

/** Parse the contract's `sectionOrder` string into full settings. */
export const sectionsFromOrder = (order: string): SiteSectionSetting[] => {
  const visible = order.split(',').map((s) => s.trim()).filter((s): s is HomeSectionKey =>
    (HOME_SECTION_KEYS as readonly string[]).includes(s),
  );
  const rest = HOME_SECTION_KEYS.filter((k) => !visible.includes(k));
  return [...visible.map((key) => ({ key, visible: true })), ...rest.map((key) => ({ key, visible: false }))];
};

export const sectionOrderString = (sections: SiteSectionSetting[]): string =>
  sections.filter((s) => s.visible).map((s) => s.key).join(',');

const normalizeSections = (v: unknown, fallback: string): SiteSectionSetting[] => {
  if (!Array.isArray(v)) return sectionsFromOrder(fallback);
  const seen = new Set<HomeSectionKey>();
  const out: SiteSectionSetting[] = [];
  for (const item of v) {
    const key = (item as { key?: unknown })?.key;
    if (typeof key !== 'string' || !(HOME_SECTION_KEYS as readonly string[]).includes(key)) continue;
    const k = key as HomeSectionKey;
    if (seen.has(k)) continue;
    seen.add(k);
    out.push({ key: k, visible: bool((item as { visible?: unknown }).visible, true) });
  }
  for (const k of HOME_SECTION_KEYS) if (!seen.has(k)) out.push({ key: k, visible: false });
  return out;
};

const mediaRef = (v: unknown): SiteMediaRef | null => {
  if (!v || typeof v !== 'object') return null;
  const m = v as Record<string, unknown>;
  if (typeof m.id !== 'string') return null;
  return {
    id: m.id,
    kind: m.kind === 'video' ? 'video' : 'image',
    url: typeof m.url === 'string' ? m.url : null,
    playback_id: typeof m.playback_id === 'string' ? m.playback_id : null,
    width: typeof m.width === 'number' ? m.width : null,
    height: typeof m.height === 'number' ? m.height : null,
    alt: typeof m.alt === 'string' ? m.alt : null,
    duration_ms: typeof m.duration_ms === 'number' ? m.duration_ms : null,
    caption: typeof m.caption === 'string' ? m.caption : null,
  };
};

const mergeCopy = (base: SiteCopy, patch: SiteCopyPatch | undefined): SiteCopy => {
  if (!patch || typeof patch !== 'object') return base;
  const out = { ...base } as SiteCopy;
  (Object.keys(base) as Array<keyof SiteCopy>).forEach((section) => {
    const p = patch[section];
    if (!p || typeof p !== 'object') return;
    const merged: Record<string, unknown> = { ...(base[section] as Record<string, unknown>) };
    Object.entries(p).forEach(([k, v]) => {
      if (v == null) return;
      if (Array.isArray(merged[k])) {
        if (Array.isArray(v)) merged[k] = v.filter((x) => typeof x === 'string');
      } else if (typeof v === 'string') merged[k] = v;
    });
    (out as unknown as Record<string, unknown>)[section] = merged;
  });
  return out;
};

/** Template defaults ← stored config. Never throws on junk. */
export function resolveConfig(templateId: SiteTemplateId | string | null | undefined, raw: unknown): SiteConfigResolved {
  const t = getSiteTemplate(templateId);
  const d = t.defaults;
  const c = (raw && typeof raw === 'object' ? raw : {}) as Record<string, unknown>;
  const sections = normalizeSections(c.sections, d.sectionOrder);
  const heroMedia = mediaRef(c.heroMedia);
  return {
    brandName: str(c.brandName, d.brandName),
    headingFont: str(c.headingFont, d.headingFont),
    bodyFont: str(c.bodyFont, d.bodyFont),
    accent: str(c.accent, d.accent),
    darkMode: bool(c.darkMode, d.darkMode),
    heroAlign: oneOf(c.heroAlign, ['left', 'center', 'right'] as const, d.heroAlign),
    galleryLayout: oneOf(c.galleryLayout, ['grid', 'masonry', 'full'] as const, d.galleryLayout),
    navStyle: oneOf(c.navStyle, ['bar', 'hamburger', 'stacked'] as const, d.navStyle),
    logoPlacement: oneOf(c.logoPlacement, ['left', 'center', 'right'] as const, d.logoPlacement),
    sections,
    sectionOrder: sectionOrderString(sections),
    stickyCta: bool(c.stickyCta, d.stickyCta),
    newsletter: bool(c.newsletter, d.newsletter),
    multiStepInquiry: bool(c.multiStepInquiry, d.multiStepInquiry),
    eyebrow: str(c.eyebrow, d.eyebrow),
    headline: str(c.headline, d.headline),
    sub: str(c.sub, d.sub),
    cta1: str(c.cta1, d.cta1),
    cta2: str(c.cta2, d.cta2),
    heroImage: typeof c.heroImage === 'string' && c.heroImage ? c.heroImage : heroMedia?.url ?? null,
    heroMedia,
    reelMedia: mediaRef(c.reelMedia),
    reelCover: mediaRef(c.reelCover),
    aboutPortrait: mediaRef(c.aboutPortrait),
    copy: mergeCopy(t.copy, c.copy as SiteCopyPatch | undefined),
    contact: (c.contact && typeof c.contact === 'object' ? (c.contact as SiteContact) : {}),
    seo: (c.seo && typeof c.seo === 'object' ? (c.seo as SiteSeo) : {}),
  };
}

// ─────────────────────────────────────────────────────────────────────
// Published snapshot (compiled by publish_site_v2 / compileDraftSnapshot)
// ─────────────────────────────────────────────────────────────────────

export interface SiteProjectPublic {
  id: string;
  slug: string;
  title: string;
  category: string | null;
  client: string | null;
  year: string | null;
  location: string | null;
  role: string | null;
  intro: string | null;
  body: string | null;
  stats: Array<[string, string]>;
  featured: boolean;
  cover: SiteMediaRef | null;
  media: SiteMediaRef[];
}

export interface SiteServicePublic {
  id: string;
  name: string;
  price: string | null;
  duration: string | null;
  includes: string[];
  blurb: string | null;
  featured: boolean;
}

export interface SiteTestimonialPublic {
  id: string;
  quote: string;
  author: string | null;
  role: string | null;
}

export interface SitePostPublic {
  id: string;
  slug: string;
  title: string;
  excerpt: string | null;
  body: string | null;
  tags: string[];
  published_at: string | null;
  read_minutes: number | null;
  cover: SiteMediaRef | null;
}

export interface SiteSnapshot {
  version: 2;
  template: SiteTemplateId;
  config: SiteConfig;
  slug: string;
  org: { name: string | null; logo_url: string | null; brand_color: string | null };
  projects: SiteProjectPublic[];
  services: SiteServicePublic[];
  testimonials: SiteTestimonialPublic[];
  posts: SitePostPublic[];
  captured_at: string;
}

/** Tolerant reader for whatever the RPC returned. */
export function parseSnapshot(raw: unknown): SiteSnapshot | null {
  if (!raw || typeof raw !== 'object') return null;
  const s = raw as Record<string, unknown>;
  const arr = <T,>(v: unknown): T[] => (Array.isArray(v) ? (v as T[]) : []);
  const org = (s.org && typeof s.org === 'object' ? s.org : {}) as Record<string, unknown>;
  return {
    version: 2,
    template: isSiteTemplateId(s.template) ? s.template : 't1',
    config: (s.config && typeof s.config === 'object' ? (s.config as SiteConfig) : {}),
    slug: typeof s.slug === 'string' ? s.slug : '',
    org: {
      name: typeof org.name === 'string' ? org.name : null,
      logo_url: typeof org.logo_url === 'string' ? org.logo_url : null,
      brand_color: typeof org.brand_color === 'string' ? org.brand_color : null,
    },
    projects: arr<SiteProjectPublic>(s.projects).map((p) => ({
      ...p,
      stats: Array.isArray(p.stats)
        ? (p.stats as unknown[]).filter((x): x is [string, string] => Array.isArray(x) && x.length >= 2).map((x) => [String(x[0]), String(x[1])])
        : [],
      media: Array.isArray(p.media) ? p.media.filter(Boolean) : [],
      cover: p.cover ?? null,
      featured: !!p.featured,
    })),
    services: arr<SiteServicePublic>(s.services).map((sv) => ({
      ...sv,
      includes: Array.isArray(sv.includes) ? sv.includes.map(String) : [],
      featured: !!sv.featured,
    })),
    testimonials: arr<SiteTestimonialPublic>(s.testimonials),
    posts: arr<SitePostPublic>(s.posts).map((p) => ({ ...p, tags: Array.isArray(p.tags) ? p.tags.map(String) : [], cover: p.cover ?? null })),
    captured_at: typeof s.captured_at === 'string' ? s.captured_at : new Date().toISOString(),
  };
}

// ─────────────────────────────────────────────────────────────────────
// Sample content (orbit-core WORK / SERVICES / TESTIMONIALS / POSTS)
// Shown in the editor preview until the studio adds their own.
// ─────────────────────────────────────────────────────────────────────

/** Tonal gradient pairs — also used as the fallback for projects
 *  without a cover, cycling by index. */
export const SAMPLE_TONES: Array<[string, string]> = [
  ['#c9c2b8', '#8f8577'],
  ['#d9d3cb', '#a89c90'],
  ['#b8bcbd', '#6f777b'],
  ['#c4bfb4', '#7a746a'],
  ['#cfc7bd', '#948a7d'],
  ['#b5b9b3', '#666d64'],
  ['#d1c6b6', '#9a8b74'],
  ['#bdbdc4', '#6d6d78'],
];

export const toneFor = (i: number): string => {
  const [a, b] = SAMPLE_TONES[((i % SAMPLE_TONES.length) + SAMPLE_TONES.length) % SAMPLE_TONES.length];
  return `linear-gradient(135deg, ${a}, ${b})`;
};

/** Prototype WORK aspect ratios (h = height/width) for the masonry sample. */
export const SAMPLE_RATIOS = [1.25, 0.75, 1, 1.33, 0.8, 1.1, 0.7, 1.2];

const SAMPLE_PROJECT_TEXT = {
  role: 'Direction, photography, edit',
  location: 'Porto, Portugal',
  intro:
    'A ceramics studio asked for a launch film and stills that felt like the inside of a kiln: slow, warm, and a little dangerous. We shot over three mornings before the studio opened.',
  body:
    'Everything was lit with one window and a single bounce. The edit follows the order of the work itself, from wet clay to the first crack of the glaze, so the film runs exactly as long as a firing cycle would feel to someone waiting.',
  stats: [['3', 'mornings'], ['1', 'window'], ['2:40', 'runtime'], ['48', 'stills']] as Array<[string, string]>,
};

export const SAMPLE_PROJECTS: SiteProjectPublic[] = [
  ['Salt Flats', 'Editorial', '2026'],
  ['Ines & Theo', 'Wedding', '2026'],
  ['North Shore', 'Commercial', '2025'],
  ['Atelier Mora', 'Brand film', '2025'],
  ['Quiet Hours', 'Portrait', '2025'],
  ['Tidewater', 'Real estate', '2024'],
  ['Copper Season', 'Documentary', '2024'],
  ['Meridian Line', 'Music video', '2024'],
].map(([title, category, year], i) => ({
  id: `sample-${i + 1}`,
  slug: title.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/(^-|-$)/g, ''),
  title,
  category,
  client: title === 'Atelier Mora' ? 'Atelier Mora' : title,
  year,
  location: SAMPLE_PROJECT_TEXT.location,
  role: SAMPLE_PROJECT_TEXT.role,
  intro: SAMPLE_PROJECT_TEXT.intro,
  body: SAMPLE_PROJECT_TEXT.body,
  stats: SAMPLE_PROJECT_TEXT.stats,
  featured: i < 6,
  cover: null,
  media: [],
}));

export const SAMPLE_SERVICES: SiteServicePublic[] = [
  { id: 'sample-s1', name: 'Portrait session', price: 'from $650', duration: '2 hours', includes: ['Two locations', '40 edited images', 'Online gallery', 'Print release'], blurb: null, featured: false },
  { id: 'sample-s2', name: 'Wedding', price: 'from $4,800', duration: 'Full day', includes: ['Two shooters', 'Engagement session', '600+ images', 'Highlight film add-on'], blurb: null, featured: true },
  { id: 'sample-s3', name: 'Commercial & film', price: 'Custom quote', duration: '1–5 days', includes: ['Creative direction', 'Licensing included', 'Color grade & sound', 'Cut-downs for social'], blurb: null, featured: false },
];

export const SAMPLE_TESTIMONIALS: SiteTestimonialPublic[] = [
  { id: 'sample-t1', quote: 'Every frame felt like it had been waiting for us. We stopped noticing the camera an hour in.', author: 'Ines Duarte', role: 'Bride, Lisbon' },
  { id: 'sample-t2', quote: 'Delivered a full campaign in nine days, and the client bought the outtakes too.', author: 'Marcus Hale', role: 'Creative Director, Fold' },
  { id: 'sample-t3', quote: 'The film made my mother cry, twice. That is the whole review.', author: 'Theo Andersen', role: 'Groom, Copenhagen' },
];

export const SAMPLE_POSTS: SitePostPublic[] = [
  { id: 'sample-p1', slug: 'what-i-pack-for-a-three-day-mountain-shoot', title: 'What I pack for a three-day mountain shoot', excerpt: null, body: null, tags: ['Process'], published_at: '2026-08-12T09:00:00Z', read_minutes: 6, cover: null },
  { id: 'sample-p2', slug: 'on-shooting-weddings-at-35mm', title: 'On shooting weddings at 35mm and nothing else', excerpt: null, body: null, tags: ['Essay'], published_at: '2026-06-03T09:00:00Z', read_minutes: 4, cover: null },
  { id: 'sample-p3', slug: 'color-grading-a-brand-film-in-one-afternoon', title: 'Color grading a brand film in one afternoon', excerpt: null, body: null, tags: ['Tutorial'], published_at: '2026-05-20T09:00:00Z', read_minutes: 8, cover: null },
];

/** A complete sample snapshot for a template — what the editor
 *  preview shows before the studio has added anything. */
export function sampleSnapshot(templateId: SiteTemplateId, config: SiteConfig = {}): SiteSnapshot {
  return {
    version: 2,
    template: templateId,
    config,
    slug: 'preview',
    org: { name: null, logo_url: null, brand_color: null },
    projects: SAMPLE_PROJECTS,
    services: SAMPLE_SERVICES,
    testimonials: SAMPLE_TESTIMONIALS,
    posts: SAMPLE_POSTS,
    captured_at: new Date().toISOString(),
  };
}
